import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiCamera,
  FiHardDrive,
  FiFolder,
  FiLink,
  FiHeart,
  FiTag,
  FiShield,
  FiPlus,
  FiTrash2,
  FiX,
  FiUser,
  FiUsers,
  FiCheck,
  FiAlertCircle,
  FiAlertTriangle,
  FiEdit2,
  FiStar
} from 'react-icons/fi';
import { SkeletonAnalyticsCard, SkeletonText, Skeleton } from './Skeleton';
import Billing from './Billing';
import PageHero from './PageHero';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from './NotificationTray';
import { CORE_LOGO_URL } from '../config/brand';
import { apiFetch } from '../utils/api';
import { getFeatureFlags, toFeaturePayload } from '../utils/featureFlags';

export default function Settings() {
  const [darkMode, setDarkMode] = useState(false);
  const [themeMode, setThemeMode] = useState('light');
  const [analytics, setAnalytics] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBilling, setShowBilling] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deletionInfo, setDeletionInfo] = useState(null);
  const [passkeyRegistering, setPasskeyRegistering] = useState(false);
  const [webAuthnStatus, setWebAuthnStatus] = useState({ available: false, hasApi: false, isSecureContext: false });
  const { registerPasskey, auth, error, user, refreshUser, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [statsPeriod, setStatsPeriod] = useState('30d');
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [archivingMemberId, setArchivingMemberId] = useState(null);
  const profilePhotoInputRef = useRef(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);
  const [profilePhotoError, setProfilePhotoError] = useState('');

  // Username editing state
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, reason: '' });
  const [savingUsername, setSavingUsername] = useState(false);
  const [featurePrefs, setFeaturePrefs] = useState(() => getFeatureFlags(user));
  const [featureLoading, setFeatureLoading] = useState(false);
  const [featureSaving, setFeatureSaving] = useState(false);

  // Passkey management state
  const [passkeys, setPasskeys] = useState([]);
  const [passkeysLoading, setPasskeysLoading] = useState(false);
  const [deletingPasskeyId, setDeletingPasskeyId] = useState(null);

  useEffect(() => {
    const storedThemeMode = localStorage.getItem('themeMode');
    const legacyDark = localStorage.getItem('darkMode') === 'true';
    let mode = 'light';
    if (storedThemeMode === 'auto' || storedThemeMode === 'dark' || storedThemeMode === 'light') {
      mode = legacyDark ? 'dark' : storedThemeMode;
    } else {
      mode = legacyDark ? 'dark' : 'light';
    }
    applyTheme(mode, true);

    if (auth?.getWebAuthnSupport) {
      setWebAuthnStatus(auth.getWebAuthnSupport());
    } else {
      const available = auth.isWebAuthnSupported();
      setWebAuthnStatus({
        available,
        hasApi: available,
        isSecureContext: typeof window !== 'undefined' ? window.isSecureContext : false
      });
    }

    loadAnalytics();
    loadSubscription();
  }, [auth]);

  useEffect(() => {
    setProfilePhotoUrl(user?.profile_photo_url || null);
  }, [user?.profile_photo_url]);

  const normalizeFeaturePrefs = useCallback((source) => ({
    clients: source?.feature_clients_enabled !== undefined
      ? Boolean(source.feature_clients_enabled)
      : true,
    shares: source?.feature_shares_enabled !== undefined
      ? Boolean(source.feature_shares_enabled)
      : true,
    friends: source?.feature_friends_enabled !== undefined
      ? Boolean(source.feature_friends_enabled)
      : true,
    strip: Number(source?.feature_strip_enabled) === 1 || source?.feature_strip_enabled === true
  }), []);

  useEffect(() => {
    if (!user?.id) return;
    setFeaturePrefs(normalizeFeaturePrefs(user));
    loadFeaturePrefs();
  }, [user?.id, normalizeFeaturePrefs]);

  async function loadFeaturePrefs() {
    setFeatureLoading(true);
    try {
      const res = await apiFetch('/api/settings/features');
      if (res.ok) {
        const data = await res.json();
        setFeaturePrefs(normalizeFeaturePrefs(data));
      }
    } catch (error) {
      console.error('Failed to load feature settings:', error);
    } finally {
      setFeatureLoading(false);
    }
  }

  async function updateFeaturePref(key, value) {
    const previous = { ...featurePrefs };
    const next = { ...featurePrefs, [key]: value };
    setFeaturePrefs(next);
    setFeatureSaving(true);
    try {
      const res = await apiFetch('/api/settings/features', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toFeaturePayload(next))
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error?.message || 'Failed to update preferences');
      }
      const data = await res.json();
      setFeaturePrefs(normalizeFeaturePrefs(data));
      await refreshUser();
    } catch (error) {
      setFeaturePrefs(previous);
      showToast(error.message || 'Failed to update preferences', 'error');
    } finally {
      setFeatureSaving(false);
    }
  }

  async function loadSubscription() {
    try {
      const [subRes, usageRes] = await Promise.all([
        fetch('/api/subscription', { credentials: 'include' }),
        fetch('/api/subscription/usage', { credentials: 'include' })
      ]);
      if (subRes.ok) setSubscription(await subRes.json());
      if (usageRes.ok) setUsage(await usageRes.json());
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
    try {
      const delRes = await fetch('/api/account/deletion', { credentials: 'include' });
      if (delRes.ok) {
        const data = await delRes.json();
        setDeletionInfo(data.deletion || null);
      }
    } catch { /* non-fatal */ }
  }

  async function handleDeleteAccount() {
    setDeleteError(null);
    setDeletingAccount(true);
    try {
      const res = await fetch('/api/account', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm: deleteConfirm })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setShowDeleteAccount(false);
        await loadSubscription();
      } else {
        setDeleteError(data.error || 'Could not delete account. Please try again.');
      }
    } catch (err) {
      console.error('Delete account failed:', err);
      setDeleteError('Could not delete account. Please try again.');
    } finally {
      setDeletingAccount(false);
    }
  }

  async function loadAnalytics() {
    try {
      const response = await fetch('/api/analytics', { credentials: 'include' });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  }

  const isTopTier = !!subscription && (
    subscription.tier === 'agency-team-8tb' ||
    String(subscription.tierName || '').toLowerCase().includes('8tb')
  );
  const teamLimit = subscription?.limits?.maxTeamMembers ?? null;
  const teamRemaining = teamLimit === null ? null : Math.max(0, teamLimit - teamMembers.length);
  const teamAtLimit = teamLimit !== null && teamMembers.length >= teamLimit;
  const canManageTeam = typeof isAdmin === 'function' ? isAdmin() : false;

  // Check username availability with debounce
  const checkUsername = useCallback(async (username) => {
    if (!username || username.length < 3) {
      setUsernameStatus({ checking: false, available: null, reason: '' });
      return;
    }

    setUsernameStatus({ checking: true, available: null, reason: '' });

    try {
      const res = await fetch(`/api/auth/username/check?username=${encodeURIComponent(username)}`, {
        credentials: 'include'
      });
      const data = await res.json();
      setUsernameStatus({
        checking: false,
        available: data.available,
        reason: data.reason || ''
      });
    } catch (err) {
      setUsernameStatus({ checking: false, available: null, reason: 'Failed to check' });
    }
  }, []);

  // Debounce username check
  useEffect(() => {
    if (!editingUsername || !newUsername) return;

    const timer = setTimeout(() => {
      checkUsername(newUsername);
    }, 400);

    return () => clearTimeout(timer);
  }, [newUsername, editingUsername, checkUsername]);

  async function saveUsername() {
    if (!newUsername || !usernameStatus.available) return;

    setSavingUsername(true);
    try {
      const res = await fetch('/api/auth/username', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username: newUsername })
      });

      if (res.ok) {
        showToast('Username updated successfully!');
        setEditingUsername(false);
        setNewUsername('');
        // Refresh user data
        if (refreshUser) refreshUser();
      } else {
        const data = await res.json();
        showToast(data.message || 'Failed to update username', 'error');
      }
    } catch (err) {
      showToast('Failed to update username', 'error');
    } finally {
      setSavingUsername(false);
    }
  }

  function startEditingUsername() {
    setNewUsername(user?.username?.includes('@') ? '' : (user?.username || ''));
    setEditingUsername(true);
    setUsernameStatus({ checking: false, available: null, reason: '' });
  }

  function cancelEditingUsername() {
    setEditingUsername(false);
    setNewUsername('');
    setUsernameStatus({ checking: false, available: null, reason: '' });
  }

  async function handleProfilePhotoChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setProfilePhotoError('Please upload an image file');
      showToast('Please upload an image file', 'error');
      if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setProfilePhotoError('Profile photo must be under 5MB');
      showToast('Profile photo must be under 5MB', 'error');
      if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = '';
      return;
    }

    setProfilePhotoUploading(true);
    setProfilePhotoError('');
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/profile/photo', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = data.message || 'Failed to upload profile photo';
        setProfilePhotoError(message);
        showToast(message, 'error');
        return;
      }

      setProfilePhotoUrl(data.url || '/api/profile/photo');
      showToast('Profile photo updated');
      if (refreshUser) refreshUser();
    } catch (err) {
      setProfilePhotoError('Failed to upload profile photo');
      showToast('Failed to upload profile photo', 'error');
    } finally {
      setProfilePhotoUploading(false);
      if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = '';
    }
  }

  async function handleProfilePhotoRemove() {
    setProfilePhotoUploading(true);
    setProfilePhotoError('');
    try {
      const res = await fetch('/api/profile/photo', {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = data.message || 'Failed to remove profile photo';
        setProfilePhotoError(message);
        showToast(message, 'error');
        return;
      }
      setProfilePhotoUrl(null);
      showToast('Profile photo removed');
      if (refreshUser) refreshUser();
    } catch (err) {
      setProfilePhotoError('Failed to remove profile photo');
      showToast('Failed to remove profile photo', 'error');
    } finally {
      setProfilePhotoUploading(false);
    }
  }

  // Check if current username looks like an email (needs to be set)
  const needsUsername = user?.username?.includes('@');
  const profileInitial = (user?.display_name || user?.username || user?.email || 'U')
    .toString()
    .charAt(0)
    .toUpperCase();
  const tenantContext = auth?.tenantContext || null;

  useEffect(() => {
    if (activeTab !== 'team') return;
    if (!tenantContext?.id) return;
    loadTeamMembers();
  }, [activeTab, tenantContext?.id]);

  // Load passkeys when security tab is active
  useEffect(() => {
    if (activeTab !== 'security') return;
    loadPasskeys();
  }, [activeTab]);

  function applyTheme(mode, silent = false) {
    const root = document.documentElement;
    const body = document.body;
    setThemeMode(mode);
    localStorage.setItem('themeMode', mode);
    const setLegacy = (on) => localStorage.setItem('darkMode', on ? 'true' : 'false');

    if (mode === 'dark') {
      setDarkMode(true);
      setLegacy(true);
      root.classList.add('dark');
      body?.classList.add('dark');
    } else if (mode === 'light') {
      setDarkMode(false);
      setLegacy(false);
      root.classList.remove('dark');
      body?.classList.remove('dark');
    } else {
      const isDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
      setDarkMode(isDark);
      setLegacy(isDark);
      root.classList.toggle('dark', isDark);
      body?.classList.toggle('dark', isDark);
    }
    if (!silent) showToast(`Theme set to ${mode}`);
  }

  function formatBytes(bytes) {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    const decimals = value >= 100 ? 0 : value >= 10 ? 1 : 2;
    return `${value.toFixed(decimals)} ${sizes[i]}`;
  }

  function formatNumber(num) {
    if (!num) return '0';
    return new Intl.NumberFormat().format(num);
  }

  async function handleRegisterPasskey() {
    setPasskeyRegistering(true);
    try {
      await registerPasskey();
      showToast('Passkey registered successfully', 'success');
      // Reload passkeys list
      await loadPasskeys();
    } catch (error) {
      console.error('Failed to register passkey:', error);
      showToast('Failed to register passkey', 'error');
    } finally {
      setPasskeyRegistering(false);
    }
  }

  async function loadPasskeys() {
    if (!auth?.getPasskeys) return;
    setPasskeysLoading(true);
    try {
      const credentials = await auth.getPasskeys();
      setPasskeys(credentials);
    } catch (error) {
      console.error('Failed to load passkeys:', error);
    } finally {
      setPasskeysLoading(false);
    }
  }

  async function handleDeletePasskey(credentialId) {
    if (!auth?.deletePasskey) return;
    setDeletingPasskeyId(credentialId);
    try {
      await auth.deletePasskey(credentialId);
      showToast('Passkey deleted successfully', 'success');
      // Reload passkeys list
      await loadPasskeys();
    } catch (error) {
      console.error('Failed to delete passkey:', error);
      showToast('Failed to delete passkey', 'error');
    } finally {
      setDeletingPasskeyId(null);
    }
  }

  async function loadTeamMembers() {
    if (!tenantContext?.id) return;
    setTeamLoading(true);
    setTeamError('');
    try {
      const response = await fetch('/api/team/members', { credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (response.status === 403) {
        setTeamMembers([]);
        return;
      }
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load team members');
      }
      const members = Array.isArray(data.members) ? data.members : [];
      const normalized = members.map((member) => {
        const role = member.role || (Array.isArray(member.roles) ? member.roles[0] : member.roles) || 'member';
        return {
          id: member.user_id || member.id,
          email: member.email,
          display_name: member.display_name || member.username || member.email,
          role,
          roles: Array.isArray(member.roles) ? member.roles : [role],
          joined_at: member.last_synced_at || member.joined_at || null,
          status: member.status || null
        };
      });
      setTeamMembers(normalized);
    } catch (err) {
      setTeamError(err.message || 'Failed to load team members');
    } finally {
      setTeamLoading(false);
    }
  }

  async function handleInviteSubmit(event) {
    event.preventDefault();
    if (!inviteEmail || !tenantContext?.id || !auth?.makeAuthenticatedRequest) return;
    setInviteSending(true);
    setInviteError('');
    try {
      const response = await auth.makeAuthenticatedRequest(
        `${auth.baseUrl}/tenants/${tenantContext.id}/invites`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: inviteEmail.trim(),
            roles: ['member'],
            invite_base_url: `${window.location.origin}/login`
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invite');
      }
      const link = data.invite_link || '';
      setInviteLink(link);
      setInviteEmail('');

      // Send invite email via Photos backend
      if (link) {
        try {
          const emailRes = await fetch('/api/team/send-invite', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email: inviteEmail.trim(), inviteLink: link })
          });
          if (emailRes.ok) {
            showToast(`Invite email sent to ${inviteEmail.trim()}`);
          } else {
            showToast('Invite created, email delivery failed. Share the link manually.', 'warning');
          }
        } catch {
          showToast('Invite created, email delivery failed. Share the link manually.', 'warning');
        }
      } else {
        showToast('Invite created');
      }
    } catch (err) {
      setInviteError(err.message || 'Failed to send invite');
    } finally {
      setInviteSending(false);
    }
  }

  async function archiveTeamMember(member) {
    if (!auth?.makeAuthenticatedRequest || !member?.id) return;
    if (!tenantContext?.id) {
      showToast('Missing tenant context. Please sign in again.', 'error');
      return;
    }
    if (member.id === user?.id) {
      showToast('You cannot archive your own account.', 'error');
      return;
    }
    const confirmed = window.confirm(`Archive ${member.email}? They will no longer be able to sign in.`);
    if (!confirmed) return;

    setArchivingMemberId(member.id);
    try {
      const suspendRes = await auth.makeAuthenticatedRequest(
        `${auth.baseUrl}/api/auth-users/${member.id}/suspend`,
        { method: 'PUT' }
      );
      if (!suspendRes.ok) {
        const data = await suspendRes.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to archive member');
      }

      await auth.makeAuthenticatedRequest(
        `${auth.baseUrl}/tenants/${tenantContext.id}/users/${member.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'suspended' })
        }
      ).catch(() => {});

      showToast('Member archived');
      loadTeamMembers();
    } catch (err) {
      showToast(err.message || 'Failed to archive member', 'error');
    } finally {
      setArchivingMemberId(null);
    }
  }

  async function changeTeamMemberRole(member, newRole) {
    if (!member?.id || member.id === user?.id) return;
    try {
      const res = await fetch(`/api/team/member/${member.id}/role`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update role');
      }
      showToast(`${member.display_name || member.email} is now ${newRole}`);
      loadTeamMembers();
    } catch (err) {
      showToast(err.message || 'Failed to update role', 'error');
    }
  }

  async function handleCopyInvite() {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      showToast('Invite link copied');
    } catch (err) {
      showToast('Failed to copy invite link', 'error');
    }
  }

  const tabClass = (isActive) =>
    `px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
      isActive
        ? 'bg-[#fcefb3] text-[#16211f] border border-[#f0e0a0] font-bold'
        : 'text-gray-600 hover:bg-gray-100 border border-transparent'
    }`;

  const periodClass = (isActive) =>
    `px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
      isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
    }`;

  return (
    <>
      <PageHero
        eyebrow="Preferences"
        title="Settings"
      >
        {subscription && (
          <div className="page-hero-actions">
            {!isTopTier && (
              <button onClick={() => setShowBilling(true)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                Upgrade
              </button>
            )}
            <button onClick={() => setShowBilling(true)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors">
              Manage Billing
            </button>
          </div>
        )}
      </PageHero>
      <div className="page-surface page-surface--padded">

        {/* User Account Card */}
        {user && (
          <div className="mb-6">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <img src="/favicon/favicon-96x96.png" alt="Account" className="h-7 w-auto" />
              <div>
                <div className="font-semibold text-gray-900">{user.display_name || user.username}</div>
                <div className="text-sm text-gray-500">{user.email || ', '}</div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-gray-200">
          <button className={tabClass(activeTab === 'profile')} onClick={() => setActiveTab('profile')}>Profile</button>
          <button className={tabClass(activeTab === 'overview')} onClick={() => setActiveTab('overview')}>Overview</button>
          <button className={tabClass(activeTab === 'security')} onClick={() => setActiveTab('security')}>Security</button>
          <button className={tabClass(activeTab === 'plan')} onClick={() => setActiveTab('plan')}>Storage & Plan</button>
          <button className={tabClass(activeTab === 'activity')} onClick={() => setActiveTab('activity')}>Activity</button>
          <button className={tabClass(activeTab === 'team')} onClick={() => setActiveTab('team')}>Team</button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiCamera className="text-blue-600" />
                Profile Photo
              </h3>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="profile-photo-preview">
                  {profilePhotoUrl ? (
                    <img src={profilePhotoUrl} alt="Profile" />
                  ) : (
                    <div className="profile-photo-placeholder">{profileInitial}</div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={profilePhotoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePhotoChange}
                    className="hidden"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => profilePhotoInputRef.current?.click()}
                      disabled={profilePhotoUploading}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {profilePhotoUploading ? 'Uploading...' : (profilePhotoUrl ? 'Replace Photo' : 'Upload Photo')}
                    </button>
                    {profilePhotoUrl && (
                      <button
                        type="button"
                        onClick={handleProfilePhotoRemove}
                        disabled={profilePhotoUploading}
                        className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Square images work best. JPG or PNG up to 5MB.
                  </p>
                  {profilePhotoError && (
                    <p className="text-sm text-red-600">{profilePhotoError}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Username Section */}
            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiUser className="text-blue-600" />
                Profile Information
              </h3>

              {/* Username Alert for email-based usernames */}
              {needsUsername && !editingUsername && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                  <FiAlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-800 font-medium">Choose a username</p>
                    <p className="text-sm text-amber-700">
                      Set a unique username so friends can find you on The Strip.
                    </p>
                  </div>
                </div>
              )}

              {/* Username Display/Edit */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  {editingUsername ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                          <input
                            type="text"
                            value={newUsername}
                            onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                            placeholder="your_username"
                            className="w-full pl-8 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            maxLength={20}
                            autoFocus
                          />
                          {usernameStatus.checking && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">checking...</span>
                          )}
                          {!usernameStatus.checking && usernameStatus.available === true && (
                            <FiCheck className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
                          )}
                          {!usernameStatus.checking && usernameStatus.available === false && (
                            <FiX className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                          )}
                        </div>
                        <button
                          onClick={saveUsername}
                          disabled={!usernameStatus.available || savingUsername}
                          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {savingUsername ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEditingUsername}
                          className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                      {usernameStatus.reason && (
                        <p className={`text-sm ${usernameStatus.available ? 'text-green-600' : 'text-red-600'}`}>
                          {usernameStatus.reason}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        3-20 characters. Letters, numbers, underscores, and hyphens only.
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 py-2 px-3 bg-white border border-gray-200 rounded-lg">
                        <span className="text-gray-400">@</span>
                        <span className={needsUsername ? 'text-gray-400 italic' : 'text-gray-900'}>
                          {needsUsername ? 'No username set' : user?.username}
                        </span>
                      </div>
                      <button
                        onClick={startEditingUsername}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200"
                      >
                        <FiEdit2 size={14} />
                        {needsUsername ? 'Set Username' : 'Change'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Email (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="py-2 px-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-600">
                    {user?.email || ', '}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Email is managed by your authentication provider.</p>
                </div>

                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                  <div className="py-2 px-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-600">
                    {user?.display_name || user?.name || ', '}
                  </div>
                </div>
              </div>
            </div>

            {/* The Strip Visibility */}
            <div className="bg-gray-50 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-2">The Strip</h4>
              <p className="text-sm text-gray-600 mb-3">
                Your username is how friends find and follow you on The Strip, our photo sharing feed.
              </p>
              {needsUsername ? (
                <p className="text-sm text-amber-600">
                  Set a username above to start sharing photos with friends.
                </p>
              ) : (
                <p className="text-sm text-green-600 flex items-center gap-2">
                  <FiCheck />
                  You can be found as <span className="font-medium">@{user?.username}</span>
                </p>
              )}
            </div>

            {/* Feature Visibility */}
            <div className="bg-gray-50 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-2">Customize your navigation</h4>
              <p className="text-sm text-gray-600 mb-4">
                Hide sections you never use. The Strip is off by default until you turn it on.
              </p>
              {featureLoading ? (
                <p className="text-sm text-gray-500">Loading preferences...</p>
              ) : (
                <div className="space-y-3">
                  <label className="flex items-center justify-between gap-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Friends</p>
                      <p className="text-xs text-gray-500">Connect with people you trust.</p>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={featurePrefs.friends}
                      onChange={() => updateFeaturePref('friends', !featurePrefs.friends)}
                      disabled={featureSaving}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">The Strip</p>
                      <p className="text-xs text-gray-500">Turning off hides your posts from friends. Turning on restores remaining posts.</p>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={featurePrefs.strip}
                      onChange={() => updateFeaturePref('strip', !featurePrefs.strip)}
                      disabled={featureSaving}
                    />
                  </label>
                  <a className="text-sm underline" href="/strip">Manage or remove existing Strip posts</a>
                  <label className="flex items-center justify-between gap-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Shares</p>
                      <p className="text-xs text-gray-500">Show public share links and access controls.</p>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={featurePrefs.shares}
                      onChange={() => updateFeaturePref('shares', !featurePrefs.shares)}
                      disabled={featureSaving}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Clients</p>
                      <p className="text-xs text-gray-500">Hide client galleries and submissions.</p>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={featurePrefs.clients}
                      onChange={() => updateFeaturePref('clients', !featurePrefs.clients)}
                      disabled={featureSaving}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delete Account danger zone (Profile tab) */}
        {activeTab === 'profile' && (
          <div className="mt-6 border border-red-200 rounded-xl p-5 bg-red-50/40">
            <h3 className="font-semibold text-red-700 mb-1">Delete account</h3>
            {deletionInfo?.status === 'scheduled' || deletionInfo?.status === 'due' ? (
              <div className="text-sm text-gray-700">
                <p className="mb-3">
                  Your account is scheduled for permanent deletion
                  {deletionInfo.scheduled_at ? ` on ${new Date(deletionInfo.scheduled_at).toLocaleDateString()}` : ' at the end of your current period'}.
                  All your photos and data will be erased. To stop this, resume your subscription in <strong>Storage &amp; Plan → Manage Billing</strong>.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Permanently delete your account and <strong>all</strong> of your photos, albums, and data. This cancels your
                  subscription and erases everything at the end of your current period. This cannot be undone.
                </p>
                <button
                  onClick={() => { setDeleteConfirm(''); setShowDeleteAccount(true); }}
                  className="px-4 py-2 bg-white text-red-700 border border-red-300 text-sm font-medium rounded-lg hover:bg-red-50"
                >
                  Delete account &amp; data
                </button>
              </>
            )}
          </div>
        )}

        {/* Delete Account confirmation modal */}
        {showDeleteAccount && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !deletingAccount && setShowDeleteAccount(false)}>
            <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-red-700 mb-2">Delete your account?</h3>
              <p className="text-sm text-gray-600 mb-4">
                This permanently deletes your account and <strong>all your photos, albums, and metadata</strong>. Your
                subscription is cancelled and everything is erased at the end of your current period, you won't be charged again.
                Export anything you want to keep first. <strong>This cannot be undone.</strong>
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-mono">{user?.email || 'DELETE'}</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 text-sm"
                placeholder={user?.email || 'DELETE'}
                autoFocus
              />
              {deleteError && <div className="text-sm text-red-600 mb-3">{deleteError}</div>}
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowDeleteAccount(false)} disabled={deletingAccount} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200">
                  Keep my account
                </button>
                <button onClick={handleDeleteAccount} disabled={deletingAccount} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-60">
                  {deletingAccount ? 'Deleting…' : 'Delete account'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Team Tab */}
        {activeTab === 'team' && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FiUsers className="text-blue-600" />
                Team Workspace
              </h3>
              <p className="text-sm text-gray-600">
                Workspace: <span className="font-medium text-gray-900">{tenantContext?.name || ', '}</span>
              </p>
              <p className="text-xs text-gray-500">Slug: {tenantContext?.slug || 'Not set'}</p>
              {!tenantContext?.id && (
                <p className="text-sm text-amber-700 mt-3">
                  No workspace selected. Sign in again to refresh your Core tenant context.
                </p>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FiLink className="text-blue-600" />
                Invite Teammates
              </h3>
              <form onSubmit={handleInviteSubmit} className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Email address"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={inviteSending || !tenantContext?.id}
                    required
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={inviteSending || !inviteEmail || !tenantContext?.id || teamAtLimit}
                  >
                    {inviteSending ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
                {teamLimit !== null && (
                  <p className="text-xs text-gray-500">
                    {teamMembers.length} of {teamLimit} members used
                    {teamRemaining !== null ? ` • ${teamRemaining} slots remaining` : ''}
                  </p>
                )}
                {inviteError && <p className="text-sm text-red-600">{inviteError}</p>}
                {teamAtLimit && (
                  <p className="text-sm text-amber-700">
                    Team member limit reached for this plan.
                  </p>
                )}
              </form>
              {inviteLink && (
                <div className="mt-4 p-3 bg-white border border-gray-200 rounded-lg">
                  <p className="text-xs text-gray-500">Invite link</p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1">
                    <a href={inviteLink} className="text-sm text-blue-600 break-all" target="_blank" rel="noreferrer">
                      {inviteLink}
                    </a>
                    <button
                      type="button"
                      onClick={handleCopyInvite}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200"
                    >
                      Copy link
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Team Members</h3>
              {teamLoading && <p className="text-sm text-gray-500">Loading team members...</p>}
              {!teamLoading && teamError && <p className="text-sm text-red-600">{teamError}</p>}
              {!teamLoading && !teamError && (
                <div className="space-y-3">
                  {teamMembers.length === 0 && (
                    <p className="text-sm text-gray-500">No team members found yet.</p>
                  )}
                  {teamMembers.map(member => {
                    const roleLabel = member.role || (Array.isArray(member.roles) ? member.roles[0] : member.roles) || 'member';
                    const isArchived = String(member.status || '').toLowerCase() === 'suspended';
                    const isSelf = member.id === user?.id;
                    const roleIcon = roleLabel === 'owner' ? <FiStar size={12} /> : roleLabel === 'admin' ? <FiShield size={12} /> : <FiUser size={12} />;
                    const roleColors = roleLabel === 'owner' ? 'bg-amber-100 text-amber-800' : roleLabel === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700';
                    return (
                      <div key={member.id} className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3${isSelf ? ' ring-1 ring-blue-200' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-9 h-9 text-gray-500 bg-gray-100 rounded-full shrink-0">
                            <FiUser size={18} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{member.display_name || member.email}</span>
                              {isSelf && <span className="px-1.5 py-0.5 text-xs text-blue-600 bg-blue-100 rounded">you</span>}
                              {isArchived && <span className="px-1.5 py-0.5 text-xs text-gray-500 bg-gray-100 rounded">archived</span>}
                            </div>
                            <div className="text-xs text-gray-500">
                              {member.email || ', '} · Joined {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : ', '}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {canManageTeam && !isSelf && !isArchived ? (
                            <select
                              value={roleLabel}
                              onChange={(e) => changeTeamMemberRole(member, e.target.value)}
                              className={`text-xs font-medium rounded-full pl-2 pr-6 py-1 border-0 cursor-pointer appearance-none ${roleColors}`}
                              style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
                            >
                              <option value="owner">Owner</option>
                              <option value="admin">Admin</option>
                              <option value="member">Member</option>
                            </select>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${roleColors}`}>
                              {roleIcon}
                              {roleLabel}
                            </span>
                          )}
                          {canManageTeam && !isSelf && (
                            <button
                              type="button"
                              onClick={() => archiveTeamMember(member)}
                              disabled={archivingMemberId === member.id || isArchived}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${isArchived ? 'border-gray-200 text-gray-400 bg-gray-100' : 'border-red-200 text-red-600 bg-red-50 hover:bg-red-100'} disabled:opacity-60`}
                            >
                              {isArchived ? 'Archived' : (archivingMemberId === member.id ? 'Archiving...' : 'Archive')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Account Overview */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Account Overview</h3>
                <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                  <button className={periodClass(statsPeriod === '7d')} onClick={() => setStatsPeriod('7d')}>7d</button>
                  <button className={periodClass(statsPeriod === '30d')} onClick={() => setStatsPeriod('30d')}>30d</button>
                  <button className={periodClass(statsPeriod === '90d')} onClick={() => setStatsPeriod('90d')}>90d</button>
                </div>
              </div>

              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => <SkeletonAnalyticsCard key={i} />)}
                </div>
              ) : analytics ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <StatCard icon={<FiCamera />} value={formatNumber(analytics.totalPhotos)} label="Total Photos" />
                  <StatCard icon={<FiHardDrive />} value={formatBytes(analytics.totalStorage)} label="Storage Used" />
                  <StatCard icon={<FiFolder />} value={formatNumber(analytics.totalAlbums)} label="Albums" />
                  <StatCard icon={<FiLink />} value={formatNumber(analytics.activeShares)} label="Active Shares" />
                  <StatCard icon={<FiHeart />} value={formatNumber(analytics.favoritePhotos)} label="Favorites" />
                  <StatCard icon={<FiTag />} value={formatNumber(analytics.totalTags)} label="Tags Used" />
                </div>
              ) : (
                <div className="p-4 bg-red-50 text-red-600 rounded-lg text-center">Failed to load analytics</div>
              )}
            </div>

            {/* Storage Breakdown */}
            {analytics?.breakdown && (
              <div className="bg-gray-50 rounded-lg p-5">
                <h4 className="font-semibold text-gray-900 mb-4">Storage Breakdown</h4>
                <div className="space-y-3">
                  <BreakdownRow label="Original Photos" value={formatBytes(analytics.breakdown.originals)} />
                  <BreakdownRow label="Small Thumbnails" value={formatBytes(analytics.breakdown.small)} />
                  <BreakdownRow label="Medium Variants" value={formatBytes(analytics.breakdown.medium)} />
                  <BreakdownRow label="Large Variants" value={formatBytes(analytics.breakdown.large)} />
                </div>
              </div>
            )}

            {/* Photo Management */}
            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Photo Management</h3>
              <div className="space-y-4">
                <SettingRow
                  title="Upload Quality"
                  description="Original photos are always preserved. Variants are created for web display."
                  badge="Automatic"
                  badgeColor="blue"
                />
                <SettingRow
                  title="Image Processing"
                  description="Photos are automatically resized for optimal performance"
                  badge="Enabled"
                  badgeColor="green"
                />
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Security & Authentication</h3>

              {/* Passkeys Section */}
              <div className="pb-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <FiShield className="text-blue-600" />
                      Passkeys (WebAuthn)
                    </h4>
                    <p className="text-sm text-gray-500 mt-1">
                      {passkeys.length > 0
                        ? 'Sign in securely using your device\'s fingerprint, face ID, or security key.'
                        : 'Add a passkey for faster, more secure sign-in using your device\'s fingerprint, face ID, or security key.'}
                    </p>
                  </div>
                  {webAuthnStatus.available && (
                    <button
                      onClick={handleRegisterPasskey}
                      disabled={passkeyRegistering}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      <FiPlus size={16} />
                      {passkeyRegistering ? 'Registering...' : (passkeys.length > 0 ? 'Add Another' : 'Add Passkey')}
                    </button>
                  )}
                </div>

                {/* Passkeys List */}
                {passkeysLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-12 w-full rounded-lg" />
                  </div>
                ) : passkeys.length > 0 ? (
                  <div className="space-y-2">
                    {passkeys.map((passkey) => (
                      <div
                        key={passkey.id}
                        className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <FiShield className="text-green-600" size={18} />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {passkey.nickname || 'Passkey'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Added {new Date(passkey.created_at).toLocaleDateString()}
                              {passkey.last_used_at && (
                                <> &middot; Last used {new Date(passkey.last_used_at).toLocaleDateString()}</>
                              )}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeletePasskey(passkey.id)}
                          disabled={deletingPasskeyId === passkey.id}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete passkey"
                        >
                          {deletingPasskeyId === passkey.id ? (
                            <span className="animate-spin inline-block w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full" />
                          ) : (
                            <FiTrash2 size={16} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : webAuthnStatus.available ? (
                  <p className="text-sm text-gray-500 italic">No passkeys registered yet.</p>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                    <FiAlertTriangle size={16} />
                    {webAuthnStatus.hasApi && !webAuthnStatus.isSecureContext
                      ? 'Passkeys require a secure origin. Serve over HTTPS.'
                      : 'Your browser does not support WebAuthn passkeys.'}
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Authentication Provider</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Your account is secured by DAILEY CORE's enterprise authentication system.
                  </p>
                </div>
                <img src={CORE_LOGO_URL} alt="Dailey Core" className="h-4" />
              </div>
            </div>
          </div>
        )}

        {/* Plan Tab */}
        {activeTab === 'plan' && subscription && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 font-semibold text-sm rounded-full">{subscription.tierName}</span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">{subscription.status === 'active' ? 'Active' : subscription.status}</span>
                </div>
                <span className="text-xl font-bold text-gray-900">${subscription.price}/mo</span>
              </div>

              {/* Storage Bar */}
              {String(subscription.storage.limitFormatted).toLowerCase() !== 'unlimited' && (() => {
                const storageUsedBytes = subscription.storage.used
                  ?? subscription.storage.usedBytes
                  ?? usage?.storage?.used
                  ?? usage?.storage?.usedBytes
                  ?? analytics?.totalStorage
                  ?? 0;
                const storageLimitBytes = subscription.storage.limit ?? usage?.storage?.limit ?? null;
                const rawStoragePercent = storageLimitBytes ? (storageUsedBytes / storageLimitBytes) * 100 : 0;
                const storagePercent = Number.isFinite(rawStoragePercent) ? Math.min(100, rawStoragePercent) : 0;
                const displayPercent = storagePercent >= 10
                  ? Math.round(storagePercent)
                  : Math.round(storagePercent * 10) / 10;
                const barPercent = storagePercent === 0 && storageUsedBytes > 0 ? 1 : storagePercent;
                const showStorageAlert = storagePercent >= 85;
                const alertTone = storagePercent >= 95 ? 'critical' : 'warning';

                return (
                  <div className="mb-4">
                    {showStorageAlert && (
                      <div className={`storage-alert ${alertTone}`}>
                        <FiAlertTriangle />
                        <div>
                          <strong>Storage nearly full</strong>
                          <p>You have used {displayPercent}% of your plan. Consider upgrading or deleting files.</p>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Storage Used</span>
                      <span className="text-gray-900 font-medium">
                        {formatBytes(storageUsedBytes)} of {subscription.storage.limitFormatted}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${barPercent}%`,
                          backgroundColor: storagePercent > 90 ? '#ef4444' : storagePercent > 75 ? '#f59e0b' : '#22c55e'
                        }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{displayPercent}% used</div>
                  </div>
                );
              })()}

              {/* Usage Stats */}
              {usage && (
                <div className="grid grid-cols-3 gap-4 py-4 border-t border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{formatNumber(usage.images.count)}</div>
                    <div className="text-xs text-gray-500">Photos</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{usage.albums.count}{usage.albums.limit && ` / ${usage.albums.limit}`}</div>
                    <div className="text-xs text-gray-500">Albums</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{usage.shares.count}</div>
                    <div className="text-xs text-gray-500">Shares</div>
                  </div>
                </div>
              )}

              {/* Features */}
              <div className="py-4 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Your Plan Features</h4>
                <div className="grid grid-cols-2 gap-2">
                  {subscription.features
                    .filter(f => !['apiaccess', 'teamcollaboration'].includes(String(f).toLowerCase().replace(/[\s_-]/g, '')))
                    .map(feature => (
                      <div key={feature} className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="text-green-500">✓</span>
                        {String(feature).replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                    ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {!isTopTier && (
                  <button onClick={() => setShowBilling(true)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
                    Upgrade Plan
                  </button>
                )}
                <button onClick={() => setShowBilling(true)} className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300">
                  Manage Billing
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && analytics?.recentActivity && (
          <div className="bg-gray-50 rounded-lg p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              <BreakdownRow label="Last Upload" value={analytics.recentActivity.lastUpload ? new Date(analytics.recentActivity.lastUpload).toLocaleDateString() : 'Never'} />
              <BreakdownRow label="Photos This Week" value={formatNumber(analytics.recentActivity.photosThisWeek)} />
              <BreakdownRow label="Photos This Month" value={formatNumber(analytics.recentActivity.photosThisMonth)} />
            </div>
          </div>
        )}
      </div>

      {/* Billing Modal */}
      {showBilling && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-auto relative">
            <button onClick={() => setShowBilling(false)} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg">
              <FiX size={20} />
            </button>
            <Billing />
          </div>
        </div>
      )}
    </>
  );
}

// Helper Components
function StatCard({ icon, value, label }) {
  return (
    <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4">
      <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center text-xl">
        {icon}
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
    </div>
  );
}

function BreakdownRow({ label, value }) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-200 last:border-0">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}

function SettingRow({ title, description, badge, badgeColor = 'gray' }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    gray: 'bg-gray-200 text-gray-700'
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b border-gray-200 last:border-0">
      <div>
        <h4 className="font-medium text-gray-900">{title}</h4>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${colors[badgeColor]}`}>{badge}</span>
    </div>
  );
}
